import type {
  KeytermPreparationItem,
  KeytermValidationOutcome,
  RejectedReferenceKeyterm,
  KeytermSource,
  BenchmarkKeytermMode,
} from "./types";

export interface KeytermPreparationItemView {
  outcome: KeytermValidationOutcome;
  statusLabel: string;
  rejectedTerms: RejectedReferenceKeyterm[];
}

export interface BenchmarkKeytermAttachmentView {
  terms: string[];
  state: "sent" | "withheld" | "empty";
  statusLabel: string;
  sourceLabel: string;
  termCountLabel: string;
}

export function benchmarkKeytermAttachmentView(
  terms: string[],
  mode: BenchmarkKeytermMode,
  source: KeytermSource = "reviewed",
): BenchmarkKeytermAttachmentView {
  const sourceLabel = source === "reference_derived"
    ? "Reference-derived"
    : source === "slide_context"
      ? "Published slide data"
      : "Reviewed terms";
  if (!terms.length) {
    return {
      terms: [],
      state: "empty",
      statusLabel: "No attached terms",
      sourceLabel,
      termCountLabel: "0 terms",
    };
  }
  const sendsTerms = mode === "on" || mode === "keywords" || mode === "combined";
  const variantLabel = mode === "prompt" ? "Prompt variant" : mode === "baseline" ? "Published-slide baseline" : null;
  return {
    terms,
    state: sendsTerms ? "sent" : "withheld",
    statusLabel: sendsTerms
      ? "Sent to provider"
      : variantLabel
        ? `${variantLabel} · keywords withheld`
        : "Not sent in baseline",
    sourceLabel,
    termCountLabel: `${terms.length} ${terms.length === 1 ? "term" : "terms"} frozen`,
  };
}

export function keytermPreparationItemView(
  item: KeytermPreparationItem,
): KeytermPreparationItemView {
  const rejectedTerms = item.rejected_terms ?? [];
  const outcome =
    item.validation_outcome ??
    (item.status === "failed"
      ? "failed"
      : item.terms.length
        ? "accepted"
        : "no_eligible_terms");

  if (outcome === "failed") {
    return { outcome, statusLabel: "Extraction failed", rejectedTerms };
  }
  if (outcome === "partially_accepted") {
    return {
      outcome,
      statusLabel: `Partially accepted · ${rejectedTerms.length} rejected`,
      rejectedTerms,
    };
  }
  if (outcome === "no_eligible_terms") {
    return { outcome, statusLabel: "No eligible terms", rejectedTerms };
  }
  return {
    outcome,
    statusLabel: item.cached ? "Completed · cached" : "Completed",
    rejectedTerms,
  };
}
